/**
 * The admin surface's status, rendered from `healthReportSchema` through the shared primitives.
 *
 * Same three states as the site's locations list and the same rule behind them: `ready` is reachable
 * only through a successful contract parse, so requirement 3.5 holds here too — a malformed report
 * renders an alert rather than a row of `undefined`.
 *
 * Task 14 replaced the bare `<dl>` with `Card` and `Table` from `@apuestapr/ui`, the same two the site
 * renders. A report is a list of labels and values, so each label is a `<th scope="row">` rather than a
 * column header — a header cell with a column scope would tell a reader the label governs the cells
 * below it, which is the opposite of true here.
 *
 * One consequence worth naming: `/api/health` answers **503** when the database is unreachable, and
 * this client treats any non-2xx as a failure, so a database outage lands in the error state with the
 * status rather than in a "database: unreachable" row. That is the right trade for now — the status
 * *is* the message on that endpoint (`statusForHealthReport`), and an error state that says the server
 * answered 503 is accurate. A screen that needs to distinguish the two reads the body on both paths,
 * and that is a change to the client rather than to this component.
 */
import type { HealthReport } from '@apuestapr/contracts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@apuestapr/ui';
import type { ReactElement } from 'react';

import type { ApiClient, ApiFailure } from '../api/client.ts';
import { useHealth, type HealthState } from './useHealth.ts';

export const failureMessage = (failure: ApiFailure): string => {
  switch (failure.kind) {
    case 'unreachable':
      return 'No pudimos conectar con la API. Revisa que el proceso esté corriendo.';
    case 'http':
      return `La API respondió con un error (${String(failure.status)}).`;
    case 'malformed':
      return 'La API respondió con datos que no coinciden con el contrato.';
  }
};

const HealthReportView = ({ report }: { readonly report: HealthReport }): ReactElement => (
  <Table>
    <TableBody>
      <TableRow>
        <TableHead scope="row">Servicio</TableHead>
        <TableCell>{report.status}</TableCell>
      </TableRow>
      <TableRow>
        <TableHead scope="row">Base de datos</TableHead>
        <TableCell>{report.database}</TableCell>
      </TableRow>
      <TableRow>
        <TableHead scope="row">Versión</TableHead>
        <TableCell>{report.version}</TableCell>
      </TableRow>
      <TableRow>
        <TableHead scope="row">Tiempo activo</TableHead>
        <TableCell>{report.uptimeSeconds} s</TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

/** Split from the section so every state can be asserted without a hook or a client. */
export const HealthView = ({ state }: { readonly state: HealthState }): ReactElement => {
  switch (state.status) {
    case 'loading':
      return <p role="status">Consultando el estado de la API…</p>;
    case 'error':
      return <p role="alert">{failureMessage(state.failure)}</p>;
    case 'ready':
      return <HealthReportView report={state.report} />;
  }
};

export const HealthSection = ({ client }: { readonly client: ApiClient }): ReactElement => {
  const state = useHealth(client);

  return (
    <section aria-labelledby="estado">
      <Card>
        <CardHeader>
          <CardTitle id="estado" level={2}>
            Estado de la API
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HealthView state={state} />
        </CardContent>
      </Card>
    </section>
  );
};
